<script setup lang="ts">
import { computed, onUnmounted } from 'vue';
import { storeToRefs } from 'pinia';
import ToggleSwitch from 'primevue/toggleswitch';
import MultiFormSelector from './MultiFormSelector.vue';
import Select from 'primevue/select';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import InputText from 'primevue/inputtext';
import ProgressSpinner from 'primevue/progressspinner';
import { ButtonType, CustomButton } from '@/components';
import { FormModel } from '@/models';
import { useStudyLinkStore } from '@/stores/studyLink';
import { useFormStore } from '@/stores/forms';

const studyLinkStore = useStudyLinkStore();
const formStore = useFormStore();
studyLinkStore.getStudyLinkDetails();

const { studyLink } = storeToRefs(studyLinkStore);

onUnmounted(() => {
  studyLink.value = undefined;
});

const emit = defineEmits(['onDismiss']);
const dismissPopup = () => {
  emit('onDismiss');
};

const copyLink = () => {
  navigator.clipboard.writeText(studyLink.value?.link ?? '');
};

const activateDeactivateLink = () => {
  if (studyLinkStore.studyLinkActivated) {
    studyLinkStore.activateStudyLink();
  } else {
    studyLinkStore.deactivateStudyLink();
  }
};

const resetLink = () => {
  studyLinkStore.resetStudyLink();
};

const isLoading = computed(
  () =>
    studyLinkStore.isGettingStudyLink.isLoading ||
    studyLinkStore.isActivatingStudyLink.isLoading ||
    studyLinkStore.isDeactivatingStudyLink.isLoading ||
    studyLinkStore.isResettingStudyLink.isLoading ||
    studyLinkStore.isUpdatingStudyLink.isLoading
);

const loadingText = computed(() => {
  if (studyLinkStore.isGettingStudyLink.isLoading) {
    return 'Fetching link details...';
  } else if (studyLinkStore.isActivatingStudyLink.isLoading) {
    return 'Activating the link...';
  } else if (studyLinkStore.isDeactivatingStudyLink.isLoading) {
    return 'Deactivating the link...';
  } else if (studyLinkStore.isResettingStudyLink.isLoading) {
    return 'Resetting the link...';
  } else if (studyLinkStore.isUpdatingStudyLink.isLoading) {
    return 'Updating the link permissions...';
  }
  return '';
});

const onViewFormsChangeListner = (updatedForms: FormModel[]) => {
  studyLinkStore.updatedStudyLink!.permissions.forms = updatedForms.map(
    form => form.base64Id
  ) as string[];
};

const onEditFormsChangeListner = (updatedForms: FormModel[]) => {
  studyLinkStore.updatedStudyLink!.permissions.editForms = updatedForms.map(
    form => form.base64Id
  ) as string[];
};

const onViewEntriesChangeListener = () => {
  studyLinkStore.updatedStudyLink!.permissions.viewEntries =
    studyLinkStore.selectedViewEntryPermission ===
    studyLinkStore.viewEntriesPermissions[0];
};

const isPristine = computed(() => {
  const initial = studyLink.value!.permissions;
  const updated = JSON.parse(
    JSON.stringify(studyLinkStore.updatedStudyLink!.permissions)
  );
  const sortedInitialForms = (initial.forms as string[]).sort();
  const sortedUpdatedForms = (updated.forms as string[]).sort();
  const sortedInitialEditForms = (initial.editForms as string[]).sort();
  const sortedUpdatedEditForms = (updated.editForms as string[]).sort();
  return (
    initial.viewEntries !== updated.viewEntries ||
    initial.export !== updated.export ||
    initial.manageInvest !== updated.manageInvest ||
    JSON.stringify(sortedInitialForms) !== JSON.stringify(sortedUpdatedForms) ||
    JSON.stringify(sortedInitialEditForms) !==
      JSON.stringify(sortedUpdatedEditForms) ||
    initial.viewEntries !== updated.viewEntries
  );
});

const submitLinkUpdates = () => {
  const updatedStudData = JSON.parse(
    JSON.stringify(studyLinkStore.updatedStudyLink)
  );
  if (
    (updatedStudData.permissions.forms as string[]).length ===
    formStore.formList.length
  ) {
    updatedStudData.permissions.forms = true;
  }
  if (
    (updatedStudData.permissions.editForms as string[]).length ===
    formStore.formList.length
  ) {
    updatedStudData.permissions.editForms = true;
  }
  studyLinkStore.updateStudyLinkPermissions(updatedStudData);
};
</script>

<template class="template-root">
  <div class="content-root">
    <div class="link-section link-toggle">
      <h3 class="title">Invite to study via link</h3>
      <ToggleSwitch v-model="studyLinkStore.studyLinkActivated"
        @change="activateDeactivateLink"/>
    </div>
    <div class="link-section copy-link"
      v-if="studyLinkStore.studyLinkActivated">
      <InputGroup>
        <InputText :value="studyLink?.link" type="text" disabled="true" />
        <InputGroupAddon>
          <button v-ripple @click="copyLink">Copy link</button>
        </InputGroupAddon>
      </InputGroup>
    </div>
    <div class="link-description" v-if="studyLinkStore.studyLinkActivated">
      Share this link to invite people to this study. You can
      <a class="reset" @click="resetLink">reset the link</a>
      to generate a new invite link.
    </div>
    <hr v-if="studyLinkStore.studyLinkActivated" />
    <div class="permissions" v-if="studyLink">
      <h3 class="title">Permissions</h3>
      <div class="link-section permission">
        <div>View forms</div>
        <MultiFormSelector :selectedFormsIds="studyLink!.permissions.forms as string[]"
          @onFormChangeListener="onViewFormsChangeListner"/>
      </div>
      <div class="link-section permission">
        <div>Add entries</div>
        <MultiFormSelector :selectedFormsIds="studyLink!.permissions.editForms as string[]"
          @onFormChangeListener="onEditFormsChangeListner"/>
      </div>
      <div class="link-section permission">
        <div>View entries</div>
        <Select v-model="studyLinkStore.selectedViewEntryPermission"
          @change="onViewEntriesChangeListener"
          placeholder="Select"
          :options="studyLinkStore.viewEntriesPermissions"/>
      </div>
      <div class="link-section permission">
        <div>Allow data export</div>
        <ToggleSwitch v-model="studyLinkStore.updatedStudyLink!.permissions.export"/>
      </div>
      <div class="link-section permission">
        <div>Allow study and users management</div>
        <ToggleSwitch v-model="studyLinkStore.updatedStudyLink!.permissions.manageInvest"/>
      </div>

      <div class="buttons">
        <div class="buttons-right">
          <CustomButton text="Cancel"
            @onClick="dismissPopup"
            :type="ButtonType.OUTLINED"/>
          <CustomButton text="Save"
            :margin-left="16"
            @onClick="submitLinkUpdates"
            :disabled="!isPristine"
            :type="ButtonType.POSITIVE"/>
        </div>
      </div>
    </div>
  </div>
  <div class="content-loading" v-if="isLoading">
    <ProgressSpinner strokeWidth="4" />
    <div>{{ loadingText }}</div>
  </div>
</template>

<style scoped lang="scss">
@import "../../assets/main.scss";
.template-root {
  position: relative;
}
.content-root {
  padding: 20px;
  width: 500px;
}
.content-loading {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background-color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.link-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 8px;
  padding-bottom: 8px;
}
.title {
  font-weight: 700;
}
.copy-link {
  :deep(.p-inputtext) {
    font-size: 14px;
    cursor: text;
  }
  :deep(.p-inputgroupaddon) {
    padding: 0px;

    button {
      padding: 0.5rem 0.75rem;
      border: none;
      background: none;
      cursor: pointer;
    }

    &:hover {
      cursor: pointer;
      background-color: hsla(0, 0%, 0%, 0.15);
    }
  }
}
.link-description {
  font-size: 14px;
  font-weight: 400;

  .reset {
    color: $primary-color;
    cursor: pointer;
    &:hover {
      background-color: hsla(0, 0%, 0%, 0.15);
    }
  }
}
hr {
  border-color: hsla(0, 0%, 0%, 0.1);
  margin: 1.5rem 0rem;
}
.permission {
  position: relative;
}
.buttons {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;

  .buttons-right {
    display: flex;
  }
}
:deep(.p-progressspinner) {
  height: 50px;
  width: 50px;
}
:deep(.p-progressspinner-circle) {
  stroke: $primary-color !important;
}
</style>
